/**
 * Background cleanup logic for expired offline editor databases.
 *
 * This module handles checking all databases from other documents and taking action:
 * - Deletes fully-expired databases
 * - Clears expired data while preserving fresh data
 * - Migrates pre-migration databases by adding timestamps
 */

import type { IDBPDatabase } from 'idb';
import { openDB } from 'idb';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import {
	OFFLINE_STEPS_DATABASE_NAME_PREFIX,
	OFFLINE_META_STORE_NAME,
	OFFLINE_STEPS_STORE_NAME,
	OFFLINE_STEPS_DATABASE_VERSION,
	type StepsDBSchema,
	type VersionInsertionBackup,
} from './database-schema.ts';
import { fireOfflineStorageAnalyticsEvent, parseErrorMessage } from './analytics.ts';
import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType.ts';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const BACKGROUND_CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000;
const MAX_DATABASES_PER_CLEANUP = 100; // Cap to prevent performance issues on first load

/**
 * Extract contentId from database name by removing the prefix.
 */
const extractContentId = (dbName: string): string =>
	dbName.replace(OFFLINE_STEPS_DATABASE_NAME_PREFIX, '');

interface MetaData {
	doc: JSONDocNode | null;
	version: number | null;
	lastUpdated?: number;
	versionInsertionBackup?: VersionInsertionBackup;
}

export interface DatabaseCheckResult {
	dbName: string;
	deleted: boolean; // True if database was deleted
	reason:
		| 'empty' // Database had no data
		| 'expired' // Database expired and was deleted
		| 'migrated' // Added timestamp to pre-migration data
		| 'active' // Not expired, no action taken
		| 'invalid' // Database missing required object stores
		| 'error'; // Error checking database
	// Optional metadata for analytics (populated for expired/error cases):
	daysExpired?: number;
	stepsCount?: number;
	hasBackup?: boolean;
	errorMessage?: string;
}

/**
 * Check if enough time has passed since the last background cleanup attempt.
 * Uses storage manager to track the last attempt timestamp.
 */
export function shouldRunBackgroundCleanup(): boolean {
	try {
		if (!confluenceLocalStorageInstance) {
			return false;
		}

		const lastAttempt = confluenceLocalStorageInstance.getItem(
			keys.EDITOR_OFFLINE_LAST_BACKGROUND_CLEANUP_ATTEMPT,
		);

		if (!lastAttempt) {
			return true;
		}

		const timeSinceLastAttempt = Date.now() - parseInt(lastAttempt, 10);

		return timeSinceLastAttempt > BACKGROUND_CLEANUP_INTERVAL_MS;
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
	} catch (error) {
		// Storage access failed - return false to skip cleanup (non-critical)
		return false;
	}
}

/**
 * Mark that a background cleanup attempt is starting.
 * Called immediately before starting cleanup to prevent race condition between tabs.
 * Acts as a distributed lock via localStorage - if multiple tabs open simultaneously,
 * only the first will proceed with cleanup.
 */
export function markBackgroundCleanupAttempted(): void {
	try {
		if (!confluenceLocalStorageInstance) {
			return;
		}

		confluenceLocalStorageInstance.setItem(
			keys.EDITOR_OFFLINE_LAST_BACKGROUND_CLEANUP_ATTEMPT,
			Date.now().toString(),
		);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
	} catch (error) {
		// Storage access failed - continue silently (non-critical)
	}
}

/**
 * Delete an entire database.
 */
async function deleteDatabase(dbName: string): Promise<void> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.deleteDatabase(dbName);

		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error);
		request.onblocked = () => resolve();
	});
}

/**
 * Add timestamp to pre-migration data to migrate it.
 */
async function addTimestampToMigrateData(
	database: IDBPDatabase<StepsDBSchema>,
	metaData?: MetaData,
): Promise<void> {
	const tx = database.transaction(OFFLINE_META_STORE_NAME, 'readwrite');
	const metaStore = tx.objectStore(OFFLINE_META_STORE_NAME);

	const updatedMetaData: MetaData = {
		...(metaData || { doc: null, version: null }),
		lastUpdated: Date.now(),
	};

	await metaStore.put(updatedMetaData as StepsDBSchema['meta']['value'], 1);
	await tx.done;
}

/**
 * Check if a database has expired and delete it if so.
 *
 * Actions:
 * - Invalid (missing required object stores) → delete
 * - Empty (no metadata and no steps) → delete
 * - Expired (lastUpdated > 30 days) → delete
 * - Missing metadata or timestamp → add timestamp (migrate)
 * - Active (lastUpdated ≤ 30 days) → keep
 */
export async function checkDatabaseExpiry(dbName: string): Promise<DatabaseCheckResult> {
	try {
		const db = await openDB<StepsDBSchema>(dbName, OFFLINE_STEPS_DATABASE_VERSION, {
			upgrade(database) {
				// Create stores if they don't exist
				if (!database.objectStoreNames.contains(OFFLINE_STEPS_STORE_NAME)) {
					database.createObjectStore(OFFLINE_STEPS_STORE_NAME, {
						keyPath: 'id',
						autoIncrement: true,
					});
				}

				if (!database.objectStoreNames.contains(OFFLINE_META_STORE_NAME)) {
					database.createObjectStore(OFFLINE_META_STORE_NAME, {});
				}
			},
			blocking: (_currentVersion, _blockedVersion, event) => {
				// Close the database connection if the database is deleted or version changes
				// This is important for Firefox compatibility when deleting databases. See HOT-12395 for more details.
				if (event?.target) {
					(event.target as IDBPDatabase<StepsDBSchema>).close();
				}
			},
		});

		try {
			// Check if both stores exist - if not, database is invalid
			if (
				!db.objectStoreNames.contains(OFFLINE_META_STORE_NAME) ||
				!db.objectStoreNames.contains(OFFLINE_STEPS_STORE_NAME)
			) {
				db.close();
				await deleteDatabase(dbName);

				return {
					dbName,
					deleted: true,
					reason: 'invalid',
					errorMessage: 'Database missing required object stores',
				};
			}

			const tx = db.transaction([OFFLINE_META_STORE_NAME, OFFLINE_STEPS_STORE_NAME], 'readonly');
			const metaStore = tx.objectStore(OFFLINE_META_STORE_NAME);
			const stepsStore = tx.objectStore(OFFLINE_STEPS_STORE_NAME);

			const metaData = (await metaStore.get(1)) as MetaData | undefined;
			const stepsCount = await stepsStore.count();

			// Empty database: No meta data AND no steps. Safe to delete.
			if (!metaData && stepsCount === 0) {
				db.close();
				await deleteDatabase(dbName);

				return {
					dbName,
					deleted: true,
					reason: 'empty',
				};
			}

			// Migration: Missing metadata or missing timestamp. Add timestamp and skip deletion.
			if (!metaData || !metaData.lastUpdated) {
				await addTimestampToMigrateData(db, metaData);

				return {
					dbName,
					deleted: false,
					reason: 'migrated',
				};
			}

			// At this point, metaData.lastUpdated is guaranteed to exist (migrated above)
			const currentTimestamp = Date.now();
			const databaseAge = currentTimestamp - metaData.lastUpdated!;
			const isExpired = databaseAge > THIRTY_DAYS_MS;

			// Expired: Delete entire database.
			if (isExpired) {
				const daysExpired = Math.floor(databaseAge / (24 * 60 * 60 * 1000));
				const hasBackup = !!metaData.versionInsertionBackup;

				db.close();
				await deleteDatabase(dbName);

				return {
					dbName,
					deleted: true,
					reason: 'expired',
					daysExpired,
					stepsCount,
					hasBackup,
				};
			}

			// Not expired: Database is active.
			return {
				dbName,
				deleted: false,
				reason: 'active',
			};
		} catch (error) {
			return {
				dbName,
				deleted: false,
				reason: 'error',
				errorMessage: parseErrorMessage(error),
			};
		} finally {
			db.close();
		}
	} catch (error) {
		// Can't open the database - treat as error case
		return {
			dbName,
			deleted: false,
			reason: 'error',
			errorMessage: parseErrorMessage(error),
		};
	}
}

/**
 * Run background cleanup of all offline editor databases.
 *
 * Checks up to 100 databases (randomised order) and deletes expired ones.
 * Excludes the current document's database.
 *
 * @param currentDbName - The name of the current database to exclude from cleanup
 * @param api - The editor plugin API for firing analytics events
 */
export async function runBackgroundCleanup(
	currentDbName?: string,
	api?: PublicPluginAPI<[EditorOfflineStoragePlugin]>,
): Promise<void> {
	try {
		if (!window.indexedDB.databases) {
			return;
		}

		const allDatabases = await window.indexedDB.databases();
		const offlineEditorDatabases = allDatabases.filter(
			(dbInfo) =>
				dbInfo.name?.startsWith(OFFLINE_STEPS_DATABASE_NAME_PREFIX) &&
				dbInfo.name !== currentDbName,
		);

		const totalOfflineDatabases = offlineEditorDatabases.length;
		let databasesProcessed = 0;
		let databasesDeleted = 0;
		const cleanupDetails: Array<{
			contentId: string;
			deletionReason: 'expired' | 'empty' | 'invalid';
			daysExpired: number;
			stepsCount: number;
			hasBackup: boolean;
		}> = [];

		// Early exit if no databases to process
		if (totalOfflineDatabases === 0) {
			fireOfflineStorageAnalyticsEvent({
				api,
				action: 'OFFLINE_EDITING_BACKGROUND_CLEANUP_COMPLETED',
				databasesProcessed: 0,
				databasesDeleted: 0,
				totalOfflineDatabases: 0,
				cleanupDetails: [],
			});
			return;
		}

		// We randomise the order to ensure all databases eventually get checked. Otherwise, we'd check the first N databases every time.
		// We also cap the number of databases checked per run to prevent performance issues. For power users with large backlogs, cleanup will happen gradually over multiple days.
		const randomisedDatabases = offlineEditorDatabases.sort(() => Math.random() - 0.5);
		const databasesToCheck = randomisedDatabases.slice(0, MAX_DATABASES_PER_CLEANUP);

		for (const dbInfo of databasesToCheck) {
			const dbName = dbInfo.name!;

			try {
				const result = await checkDatabaseExpiry(dbName);
				databasesProcessed++;

				// Fire analytics for database deletions (expired, empty, or invalid)
				if (
					result.reason === 'expired' ||
					result.reason === 'empty' ||
					result.reason === 'invalid'
				) {
					databasesDeleted++;
					cleanupDetails.push({
						contentId: extractContentId(dbName),
						deletionReason: result.reason,
						daysExpired: result.daysExpired ?? 0,
						stepsCount: result.stepsCount ?? 0,
						hasBackup: result.hasBackup ?? false,
					});
				}

				// Fire analytics for database check errors
				if (result.reason === 'error') {
					fireOfflineStorageAnalyticsEvent({
						api,
						action: 'OFFLINE_EDITING_BACKGROUND_CLEANUP_ERROR',
						errorMessage: result.errorMessage ?? 'Unknown error',
						errorSource: 'database_check',
						contentId: extractContentId(dbName),
						totalOfflineDatabases,
					});
				}
			} catch (error) {
				databasesProcessed++;

				fireOfflineStorageAnalyticsEvent({
					api,
					action: 'OFFLINE_EDITING_BACKGROUND_CLEANUP_ERROR',
					errorMessage: parseErrorMessage(error),
					errorSource: 'database_check',
					contentId: extractContentId(dbName),
					totalOfflineDatabases,
				});
			}
		}

		// Fire completion analytics for every cleanup run
		fireOfflineStorageAnalyticsEvent({
			api,
			action: 'OFFLINE_EDITING_BACKGROUND_CLEANUP_COMPLETED',
			databasesProcessed,
			databasesDeleted,
			totalOfflineDatabases,
			cleanupDetails,
		});
	} catch (error) {
		fireOfflineStorageAnalyticsEvent({
			api,
			action: 'OFFLINE_EDITING_BACKGROUND_CLEANUP_ERROR',
			errorMessage: parseErrorMessage(error),
			errorSource: 'cleanup_init',
		});
	}
}
